import React from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import type { RouterStatus } from './RouterPill';

export type HeartbeatStatus = 'online' | 'offline';
export type CompositeStatus = 'online' | 'degraded' | 'offline';

/**
 * Composite of NodeMCU heartbeat freshness AND router WG reachability.
 * Both must be green for 🟢 Online; both red for 🔴 Offline; any single
 * signal failing is 🟡 Degraded so the operator can tell from the table
 * that something needs attention without expanding the row.
 */
export function compositeStatus(
   heartbeat: HeartbeatStatus,
   router: RouterStatus
): CompositeStatus {
   const heartbeatUp = heartbeat === 'online';
   const routerUp = router === 'connected';
   // 'agent-down' (Lite): the tunnel is alive — the box has power and
   // internet — so even with a dead NodeMCU this is degraded, not offline
   // (offline reads as "site dark"; here a reboot fixes it).
   const routerReachable = routerUp || router === 'agent-down';
   if (heartbeatUp && routerUp) return 'online';
   if (!heartbeatUp && !routerReachable) return 'offline';
   return 'degraded';
}

interface Props {
   status: HeartbeatStatus;
   routerStatus: RouterStatus;
}

const DeviceStatusBadge: React.FC<Props> = ({ status, routerStatus }) => {
   const state = compositeStatus(status, routerStatus);
   const heartbeatUp = status === 'online';
   const title =
      state === 'online'
         ? 'Online — NodeMCU + router both reachable'
         : state === 'offline'
         ? 'Offline — both NodeMCU and router unreachable'
         : routerStatus === 'agent-down'
         ? 'Degraded — router online but the SmartFi agent stopped (reboot the router)'
         : heartbeatUp
         ? 'Degraded — NodeMCU heartbeating but router unreachable'
         : 'Degraded — router reachable but NodeMCU heartbeat stale';
   return (
      <Badge $state={state} title={title}>
         {state === 'online'
            ? '🟢 Online'
            : state === 'offline'
            ? '🔴 Offline'
            : '🟡 Degraded'}
      </Badge>
   );
};

export default DeviceStatusBadge;

const Badge = styled.span<{ $state: CompositeStatus }>`
   display: inline-block;
   padding: 2px 9px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 600;
   white-space: nowrap;
   background: ${({ $state }) =>
      $state === 'online'
         ? `${colors.SUCCESS}1f`
         : $state === 'offline'
         ? `${colors.DANGER}1f`
         : `${colors.WARNING}26`};
   color: ${({ $state }) =>
      $state === 'online'
         ? colors.SUCCESS
         : $state === 'offline'
         ? colors.DANGER
         : colors.WARNING};
`;
